import {
  ButtonFilterERP,
  ButtonPrimeReact,
  CalendarPrimeReact,
  InputCurrency,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FacturaProformaQueryParamsProps } from "api/FacturaProforma.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { ButtonMenu } from "common/components/ButtonMenu/ButtonMenu"
import useCrearDocumento from "common/hooks/useEnviarDocumento"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import * as ROUTES from "routes/RoutePaths"
import { backendDateFormat } from "utils/formatDate"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
}: {
  serviceQuery: (queryParams: FacturaProformaQueryParamsProps) => void
  sortingCriteria?: SortingCriteria
  skipCount?: FacturaProformaQueryParamsProps["SkipCount"]
  maxResultCount?: FacturaProformaQueryParamsProps["MaxResultCount"]
  resetPagination?: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const { crearDocumento } = useCrearDocumento()

  const openFilters = () => {
    setMenuOpen(true)
  }

  const queryFixer = (queryParams: FacturaProformaQueryParamsProps) => {
    const facturasParams = {
      ImporteLowerLimit: amount.min,
      ImporteUpperLimit: amount.max,
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "FechaEmision DESC",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    ImporteLowerLimit: amount?.min,
    ImporteUpperLimit: amount?.max,
    FechaEmisionLowerLimit: date?.min && backendDateFormat(new Date(date.min)),
    FechaEmisionUpperLimit: date?.max && backendDateFormat(new Date(date.max)),
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setDate({ min: undefined, max: undefined })
    setAmount({
      min: undefined,
      max: undefined,
    })
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.min)
        : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.max)
        : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },
    {
      label: `${t("nsAssets.filters.date.since")}:`,
      value: date?.min
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.min))
        : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("nsAssets.filters.date.until")}:`,
      value: date?.max
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.max))
        : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters?.length,
    keyword,
    amount?.min,
    amount?.max,
    skipCount,
    maxResultCount,
    sortingCriteria?.sort,
    sortingCriteria?.property,
  ])

  return (
    <>
      <div className='tw-flex tw-flex-row tw-gap-3'>
        <SearchInput
          placeholder={`${t("filtros.placeholderSearch")}`}
          onChange={(ev: string) => {
            resetPagination?.()
            setKeyword(ev)
          }}
          inputValue={keyword}
          className='tw-h-10 tw-w-full sm:tw-w-72'
        />
        <ButtonFilterERP
          isActive={appliedFilters.length > 0}
          numberActive={appliedFilters?.length}
          onClick={openFilters}
          deleteFilter={handleResetFilters}
          filterButtonLabel={`${t("filtros.title")}`}
        />

        <ButtonMenu
          onClickButton={() => crearDocumento(ROUTES.CREAR_FACTURA_PROFORMA)}
          buttonProps={{
            children: t("nsProformaInvoices.header.newInvoice"),
          }}
          items={[
            {
              label: t("newDoc.factura.newInvoice"),
              icon: "file-invoice",
              onClick: () => crearDocumento(ROUTES.CREAR_FACTURA),
            },
          ]}
        />
      </div>
      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          dismissableMask
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelSince")}
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.min}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, min: value })}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelUntil")}
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.max}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, max: value })}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.date.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col mobile:tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col tw-flex-grow tw-mb-4 mobile:tw-mb-0'>
                <label>{t("nsAssets.filters.date.since")}</label>
                <CalendarPrimeReact
                  id='fechaDesde'
                  name='fechaDesde'
                  value={date?.min ? new Date(date.min) : undefined}
                  selectionMode='single'
                  locale={language}
                  dateFormat='dd/mm/yy'
                  readOnlyInput
                  clearButtonClassName='!tw-hidden'
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      max:
                        date?.max && new Date(eventDate) > new Date(date?.max)
                          ? undefined
                          : date?.max,
                      min: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-[2302]'
                />
              </div>
              <div className='tw-hidden mobile:tw-block tw-py-3 tw-px-3 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col tw-flex-grow'>
                <label>{t("nsAssets.filters.date.until")}</label>
                <CalendarPrimeReact
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      min:
                        date?.min && new Date(eventDate) < new Date(date?.min)
                          ? undefined
                          : date?.min,
                      max: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-[2302]'
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
